import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../services/supabase';
import type { Order, OrderStatus } from '../types';
import { useCartStore } from './cartStore';
import { useAuthStore } from './authStore';
import toast from 'react-hot-toast';

// Realtime subscription channel
let orderChannel: any = null;

interface OrderState {
    orders: Order[];
    isLoading: boolean;
    initialized: boolean;
    error: string | null;

    // Actions
    fetchOrders: () => Promise<void>;
    refreshOrders: () => Promise<void>;
    getOrderById: (id: string) => Order | undefined;
    getOrderByNumber: (orderNumber: string) => Order | undefined;
    getUserOrders: (userId: string) => Order[];
    createOrder: (orderData: Partial<Order>) => Promise<Order | null>;
    updateOrderStatus: (orderId: string, status: OrderStatus, note?: string) => Promise<boolean>;
    cancelOrder: (orderId: string, reason?: string) => Promise<boolean>;

    // Admin actions
    getAllOrders: () => Order[];
    getPendingOrders: () => Order[];
    getOrdersByStatus: (status: OrderStatus) => Order[];

    // Realtime
    subscribeToOrderChanges: () => void;
    unsubscribeFromOrderChanges: () => void;
}

export const useOrderStore = create<OrderState>()(
    persist(
        (set, get) => ({
            orders: [],
            isLoading: false,
            initialized: false,
            error: null,

            fetchOrders: async () => {
                const user = useAuthStore.getState().user;
                if (!user) return;

                set({ isLoading: true, error: null });

                try {
                    let query = supabase
                        .from('orders')
                        .select(`
                            *,
                            user:users(id, email, first_name, last_name, full_name, company_name, phone),
                            items:order_items(
                                *,
                                product:products(*)
                            )
                        `)
                        .order('created_at', { ascending: false });

                    // Eğer admin değilse sadece kendi siparişlerini çek
                    if (user.role !== 'admin') {
                        query = query.eq('user_id', user.id);
                    }

                    // 15 saniyelik zaman aşımı koruması
                    const queryPromise = query;
                    const timeoutPromise = new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('Siparişler yüklenirken zaman aşımı oluştu')), 15000)
                    );

                    const result: any = await Promise.race([queryPromise, timeoutPromise]);
                    const { data: ordersData, error } = result;

                    if (error) throw error;

                    if (ordersData) {
                        const mappedOrders: Order[] = ordersData.map((o: any) => ({
                            id: o.id,
                            orderNumber: o.order_number,
                            userId: o.user_id,
                            user: o.user ? {
                                id: o.user.id,
                                email: o.user.email,
                                firstName: o.user.first_name,
                                lastName: o.user.last_name,
                                fullName: o.user.full_name,
                                companyName: o.user.company_name,
                                phone: o.user.phone,
                                role: 'customer',
                                addresses: [],
                                createdAt: new Date(),
                            } : undefined,
                            items: o.items.map((i: any) => ({
                                id: i.id,
                                productId: i.product_id,
                                product: i.product,
                                quantity: i.quantity,
                                unitPrice: Number(i.unit_price),
                                totalPrice: Number(i.total_price),
                                notes: i.notes
                            })),
                            status: o.status,
                            statusHistory: o.status_history || [],
                            shippingAddress: o.shipping_address || {},
                            billingAddress: o.billing_address || {},
                            subtotal: Number(o.subtotal),
                            shippingCost: Number(o.shipping_cost),
                            tax: 0,
                            discount: 0,
                            total: Number(o.total),
                            paymentMethod: o.payment_method,
                            paymentStatus: o.payment_status,
                            notes: o.notes,
                            adminNotes: o.admin_notes,
                            trackingNumber: o.tracking_number,
                            carrierName: o.carrier_name,
                            estimatedDelivery: o.estimated_delivery ? new Date(o.estimated_delivery) : undefined,
                            deliveredAt: o.delivered_at ? new Date(o.delivered_at) : undefined,
                            isSample: o.is_sample || false,
                            createdAt: new Date(o.created_at),
                            updatedAt: new Date(o.updated_at),
                        }));

                        set({ orders: mappedOrders, isLoading: false, initialized: true });
                    }
                } catch (error: any) {
                    console.error('Error fetching orders:', error);
                    set({ isLoading: false, error: error.message });
                }
            },

            refreshOrders: async () => {
                await get().fetchOrders();
            },

            getOrderById: (id: string) => {
                return get().orders.find(o => o.id === id);
            },

            getOrderByNumber: (orderNumber: string) => {
                return get().orders.find(o => o.orderNumber === orderNumber);
            },

            getUserOrders: (userId: string) => {
                return get().orders
                    .filter(o => o.userId === userId)
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            },

            createOrder: async (orderData: Partial<Order>) => {
                set({ isLoading: true, error: null });
                const cart = useCartStore.getState();
                const auth = useAuthStore.getState();

                if (!auth.user) {
                    set({ isLoading: false, error: 'Oturum açmanız gerekiyor' });
                    return null;
                }

                try {
                    // Sipariş Hesabı
                    const subtotal = cart.items.reduce((sum, item) => sum + (item.product.wholesalePrice * item.quantity), 0);
                    const shippingCost = subtotal >= 5000 ? 0 : 100;
                    const total = subtotal + shippingCost;

                    // Siparişi veritabanına ekle (order_number trigger tarafından otomatik oluşturulur)
                    const { data: order, error: orderError } = await supabase
                        .from('orders')
                        .insert({
                            user_id: auth.user.id,
                            status: 'pending',
                            subtotal,
                            shipping_cost: shippingCost,
                            total,
                            payment_method: orderData.paymentMethod || 'bank_transfer',
                            payment_status: 'paid', // TEST MODU: Ödeme başarılı olarak işaretle
                            shipping_address: orderData.shippingAddress || auth.user.addresses?.[0],
                            notes: orderData.notes
                        })
                        .select()
                        .single();

                    if (orderError) throw orderError;

                    // Sipariş kalemlerini ekle
                    const orderItems = cart.items.map(item => ({
                        order_id: order.id,
                        product_id: item.productId,
                        product_name: item.product.name,
                        quantity: item.quantity,
                        unit_price: item.product.wholesalePrice,
                        total_price: item.product.wholesalePrice * item.quantity,
                    }));

                    const { error: itemsError } = await supabase
                        .from('order_items')
                        .insert(orderItems);

                    if (itemsError) throw itemsError;

                    // Sepeti temizle ve state'i güncelle
                    cart.clearCart();
                    await get().fetchOrders();

                    return get().orders.find(o => o.id === order.id) || null;

                } catch (error: any) {
                    console.error('Sipariş oluşturma hatası:', error);
                    set({ isLoading: false, error: error.message });
                    return null;
                }
            },

            updateOrderStatus: async (orderId: string, status: OrderStatus, note?: string) => {
                try {
                    // Mevcut history'i al
                    const currentOrder = get().getOrderById(orderId);
                    const newHistory = [
                        ...(currentOrder?.statusHistory || []),
                        { status, timestamp: new Date(), note }
                    ];

                    const { error } = await supabase
                        .from('orders')
                        .update({
                            status,
                            status_history: newHistory,
                            updated_at: new Date(),
                            ...(status === 'delivered' ? { delivered_at: new Date() } : {})
                        })
                        .eq('id', orderId);

                    if (error) throw error;

                    // Bildirim Oluştur (Real-time trigger simülasyonu)
                    const targetOrder = get().getOrderById(orderId);
                    if (targetOrder) {
                        const statusMap: Record<string, string> = {
                            pending: 'Onay Bekliyor',
                            confirmed: 'Onaylandı',
                            preparing: 'Hazırlanıyor',
                            in_transit: 'Yolda',
                            delivered: 'Teslim Edildi',
                            cancelled: 'İptal Edildi',
                        };
                        const statusText = statusMap[status] || status;

                        // Asenkron olarak bildirimi kaydet (Hata alsa bile akışı bozmasın)
                        supabase.from('notifications').insert({
                            user_id: targetOrder.userId,
                            type: 'order',
                            title: 'Sipariş Durumu Güncellendi',
                            message: `${targetOrder.orderNumber} nolu siparişinizin durumu "${statusText}" olarak güncellendi.${note ? ` Not: ${note}` : ''}`,
                            link: `/orders/${targetOrder.id}`,
                            is_read: false
                        }).then(({ error }: { error: any }) => {
                            if (error) console.error('Bildirim oluşturulamadı:', error);
                        });
                    }

                    await get().fetchOrders();
                    return true;
                } catch (error) {
                    console.error('Order status update error:', error);
                    return false;
                }
            },

            cancelOrder: async (orderId: string, reason?: string) => {
                return await get().updateOrderStatus(orderId, 'cancelled', reason);
            },

            getAllOrders: () => {
                return get().orders;
            },

            getPendingOrders: () => {
                return get().orders.filter(o => o.status === 'pending');
            },

            getOrdersByStatus: (status: OrderStatus) => {
                return get().orders.filter(o => o.status === status);
            },

            // Sipariş değişikliklerini real-time dinle
            subscribeToOrderChanges: () => {
                const user = useAuthStore.getState().user;
                if (!user) return;

                // Önceki subscription varsa temizle
                if (orderChannel) {
                    supabase.removeChannel(orderChannel);
                }

                // Yeni subscription oluştur
                const channelName = user.role === 'admin' 
                    ? 'all-orders-changes' 
                    : `user-orders-${user.id}`;

                orderChannel = supabase
                    .channel(channelName)
                    .on(
                        'postgres_changes',
                        {
                            event: 'INSERT',
                            schema: 'public',
                            table: 'orders',
                            ...(user.role !== 'admin' ? { filter: `user_id=eq.${user.id}` } : {})
                        },
                        async (payload) => {
                            console.log('[Realtime] New order created:', payload);
                            // Yeni sipariş eklendiğinde siparişleri yenile
                            await get().refreshOrders();
                        }
                    )
                    .on(
                        'postgres_changes',
                        {
                            event: 'UPDATE',
                            schema: 'public',
                            table: 'orders',
                            ...(user.role !== 'admin' ? { filter: `user_id=eq.${user.id}` } : {})
                        },
                        async (payload) => {
                            console.log('[Realtime] Order updated:', payload);
                            
                            const updatedOrder = payload.new as any;
                            const oldOrder = payload.old as any;

                            // Siparişleri yenile
                            await get().refreshOrders();

                            // Durum değişikliği varsa bildirim göster
                            if (updatedOrder.status !== oldOrder.status && user.role !== 'admin') {
                                const statusLabels: Record<string, string> = {
                                    pending: 'Onay Bekliyor',
                                    confirmed: 'Onaylandı',
                                    preparing: 'Hazırlanıyor',
                                    in_transit: 'Yolda',
                                    delivered: 'Teslim Edildi',
                                    cancelled: 'İptal Edildi',
                                };

                                const statusText = statusLabels[updatedOrder.status] || updatedOrder.status;
                                
                                toast.success(
                                    `Sipariş #${updatedOrder.order_number} durumu: ${statusText}`,
                                    { 
                                        duration: 5000,
                                        icon: '📦'
                                    }
                                );
                            }
                        }
                    )
                    .subscribe((status) => {
                        console.log('[Realtime] Order subscription status:', status);
                    });
            },

            // Subscription'ı kapat
            unsubscribeFromOrderChanges: () => {
                if (orderChannel) {
                    supabase.removeChannel(orderChannel);
                    orderChannel = null;
                    console.log('[Realtime] Unsubscribed from order changes');
                }
            },
        }),
        {
            name: 'cheesecake-orders',
            partialize: (state) => ({
                // Sadece gerekli şeyleri persist edelim veya hiçbirini etmeyelim
                // Siparişleri persist etmek offline kullanım için iyi olabilir
                orders: state.orders
            }),
        }
    )
);

// Sipariş durumu Türkçe çevirileri
export const orderStatusLabels: Record<OrderStatus, string> = {
    pending: 'Onay Bekliyor',
    confirmed: 'Onaylandı',
    preparing: 'Hazırlanıyor',
    in_transit: 'Yolda',
    delivered: 'Teslim Edildi',
    cancelled: 'İptal Edildi',
};

// Sipariş durumu renkleri
export const orderStatusColors: Record<OrderStatus, string> = {
    pending: '#f59e0b',
    confirmed: '#3b82f6',
    preparing: '#8b5cf6',
    in_transit: '#6366f1',
    delivered: '#10b981',
    cancelled: '#ef4444',
};
